/**
 * Desktop (OS-level) notification for a finished extraction.
 *
 * The in-app toast is the primary announcement, but a KPI extraction takes
 * minutes and the whole point of leaving it running is that the user goes back
 * to Excel or their inbox. A toast painted on a hidden tab is a message nobody
 * receives. So when — and only when — the tab is hidden, a settled run ALSO
 * fires a browser notification.
 *
 * Everything here is best-effort by design: no Notification API (jsdom, an old
 * browser), a denied permission, or a constructor that throws must all end as a
 * silent no-op. A completion announcement is not worth an exception on the
 * poll path.
 */

import type { ToastVariant } from "../components/Workflows/WorkflowToast";
import { playNotificationChime } from "./notificationChime";
import {
	hasOptedOutOfSoftAsk,
	isDesktopNotificationsEnabled,
} from "./notificationPreferences";

export interface DesktopNotifyDecision {
	/** Does this environment have the Notification API at all? */
	supported: boolean;
	/** `Notification.permission`, or null when unsupported. */
	permission: NotificationPermission | null;
	/** `document.hidden` — the tab the toast would have been painted on. */
	hidden: boolean;
	/**
	 * Is this a terminal outcome (done/failed/cancelled)?
	 *
	 * The watch also announces "still running" when it runs out of close-watch
	 * attempts. That is a status note about the app, not news about the run, and
	 * pushing it to the OS would train the user to ignore the ones that matter.
	 */
	isTerminal: boolean;
	/**
	 * The app's own preference (Settings → Notifications), independent of the
	 * browser permission. Granting Chrome the right to show banners is not the
	 * same as wanting one for every extraction.
	 *
	 * Optional so existing callers that only reason about the browser state read
	 * the same as before; absent means "not turned off".
	 */
	appEnabled?: boolean;
}

/** The whole decision, in one place, with no browser objects to mock. */
export function shouldDesktopNotify(input: DesktopNotifyDecision): boolean {
	return (
		input.supported &&
		input.permission === "granted" &&
		input.hidden &&
		input.isTerminal &&
		input.appEnabled !== false
	);
}

/** Titles are read at a glance in an OS banner — say what happened, not why. */
export function desktopTitleFor(variant: ToastVariant): string {
	if (variant === "error") return "Doppler — extraction failed";
	if (variant === "info") return "Doppler — extraction cancelled";
	return "Doppler — extraction complete";
}

const notificationApi = (): typeof Notification | null => {
	if (typeof window === "undefined") return null;
	const api = (window as Window & { Notification?: typeof Notification })
		.Notification;
	return typeof api === "function" ? api : null;
};

/** Is there a Notification API here at all? */
export function isDesktopNotificationSupported(): boolean {
	return notificationApi() !== null;
}

/** The browser's answer, or null where the API does not exist. */
export function desktopNotificationPermission(): NotificationPermission | null {
	const api = notificationApi();
	return api ? api.permission : null;
}

/**
 * Should the app offer its own "notify me when this finishes" line?
 *
 * Only while the browser answer is still open AND the user has not waved the
 * offer away twice. This is the SOFT ask — a line in a toast, not a dialog.
 */
export function shouldSoftAskForNotifications(): boolean {
	return (
		desktopNotificationPermission() === "default" && !hasOptedOutOfSoftAsk()
	);
}

/**
 * Ask for permission — only ever from a real click on the app's own offer.
 *
 * Round 1 called this when a run started, and live testing showed why that
 * fails: Chrome's quieter-notifications UI suppresses a prompt that is not
 * anchored to a user gesture the browser trusts, so the permission sat at
 * "default" forever and nothing ever appeared. Now the app asks first, in its
 * own words, and the browser dialog opens from the click on THAT.
 *
 * Resolves to the resulting permission, or null when there was nothing to ask.
 */
export function requestDesktopNotificationPermission(): Promise<NotificationPermission | null> {
	const api = notificationApi();
	if (!api || api.permission !== "default") {
		return Promise.resolve(api ? api.permission : null);
	}
	try {
		// Older Safari passes a callback instead of returning a promise; both
		// shapes are fine here because we only care about the stored permission.
		return Promise.resolve(api.requestPermission())
			.then(() => api.permission)
			.catch(() => api.permission);
	} catch {
		// A browser that throws on request is a browser we do not notify on.
		return Promise.resolve(api.permission);
	}
}

/**
 * Fire the notification. Clicking it brings the tab back and dismisses it —
 * the only useful action from an OS banner about a page you left.
 *
 * The banner and the banner only: the chime is a separate preference and is
 * the caller's to play, so that turning one off never silences the other.
 */
export function showDesktopNotification(title: string, body: string): void {
	const api = notificationApi();
	if (!api || api.permission !== "granted") return;
	try {
		const notification = new api(title, { body });
		notification.onclick = () => {
			try {
				window.focus();
			} catch {
				// Focus can be refused; closing is still worth doing.
			}
			notification.close();
		};
	} catch {
		// Constructor throws on some platforms (notably Android Chrome, which
		// requires a service worker). Silence is the correct outcome.
	}
}

/**
 * Read the live browser state and announce out-of-app if the decision says so.
 *
 * Two independent channels, one trigger. Both only fire for news about the run
 * on a tab the user has left — that part is shared — but past that gate the
 * banner answers to the browser permission and the desktop preference, and the
 * chime answers to the sound preference alone. Sound needs no permission from
 * anyone, so "chime, no banner" is a configuration that genuinely works, and
 * it is the one a user picks when the OS refuses to show banners at all.
 */
export function maybeNotifyDesktop(input: {
	text: string;
	variant: ToastVariant;
	isTerminal: boolean;
}): void {
	const hidden = typeof document !== "undefined" && document.hidden === true;
	// The in-app toast is the whole announcement on a visible tab, and a
	// non-terminal status note is not news worth leaving the page for.
	if (!input.isTerminal || !hidden) return;

	const api = notificationApi();
	const decision: DesktopNotifyDecision = {
		supported: api !== null,
		permission: api ? api.permission : null,
		hidden,
		isTerminal: input.isTerminal,
		appEnabled: isDesktopNotificationsEnabled(),
	};
	if (shouldDesktopNotify(decision)) {
		showDesktopNotification(desktopTitleFor(input.variant), input.text);
	}
	// Self-gating on the sound preference, and deliberately not on whether the
	// banner above went out.
	playNotificationChime();
}
